<script setup lang="ts">
    import { inject, ref, computed, onMounted, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import axios from 'axios';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import CustomPagination from '@/components/CustomPagination.vue';
    import { showAlert } from '@/utils/swal';

    const router = useRouter();
    const BACKEND_URL = inject('BACKEND_URL');
    const collectivite = ref<any>({});
    const membersList = ref<any[]>([]);
    const loadingMembers = ref(false);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const loadingSendInvitation = ref<{ [key: number]: boolean }>({});
    const activeTab = ref('information');
    const selectedMembers = ref<number[]>([]);

    const isCollectiviteOpen = ref(true);
    const isWalletOpen = ref(true);
    const isGerantSectionOpen = ref(true);
    const isMembresOpen = ref(true);

    const membersPagination = reactive({
        pageNumber: 1,
        total: 0,
        per_page: 10,
        last_page: 1
    });

    const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

    function formatDateFrench(dateString: string | null): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return `${date.getDate()} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`;
    }

    function formatAmountFr(amount: number | null | undefined): string {
        if (amount === null || amount === undefined) return '0 F';
        return new Intl.NumberFormat('fr-FR').format(amount) + ' F';
    }

    function calculatePercentage(value: number | null, total: number | null): number {
        if (!value || !total || total === 0) return 0;
        return Math.round((value / total) * 100 * 10) / 10;
    }

    async function fetchCollectiviteDetails() {
        try {
            const response = await axios.get('/collectivites/ma-collectivite/details');
            collectivite.value = response.data;
        } catch (error: any) {
            console.error('Erreur lors du chargement de la collectivité:', error);
        }
    }

    async function fetchMembers() {
        if (!collectivite.value.id) return;
        try {
            loadingMembers.value = true;
            const response = await axios.get('/collectivites/membres/list', {
                params: {
                    pageSize: membersPagination.per_page,
                    pageNumber: membersPagination.pageNumber,
                }
            });
            membersList.value = response.data.data || [];
            membersPagination.total = response.data.total || 0;
            membersPagination.last_page = response.data.last_page || 1;
        } catch (error: any) {
            console.error('Erreur lors du chargement des membres:', error);
        } finally {
            loadingMembers.value = false;
        }
    }

    onMounted(async () => {
        await fetchCollectiviteDetails();
        if (collectivite.value.id) {
            fetchMembers();
        }
    });

    function onMembersPaginate(pageNumber: number) {
        membersPagination.pageNumber = pageNumber;
        fetchMembers();
    }

    async function sendInvitationsUnverified(memberId: number) {
        const confirmation = await confirmationModalRef.value?.open(
            "Confirmer l'envoi des invitations",
            "Voulez-vous vraiment envoyer une invitation au membre sélectionné ?"
        );
        if (!confirmation) return;

        loadingSendInvitation.value[memberId] = true;
        try {
            await axios.post('/collectivites/membres/send_invitations_unverified', {
                users_ids: [memberId],
                collectivite_id: collectivite.value.id
            });
            showAlert.success('Invitation envoyée avec succès');
        } catch (error: any) {
            console.error("Erreur lors de l'envoi de l'invitation:", error);
            showAlert.error(error.response?.data?.message || "Une erreur est survenue lors de l'envoi de l'invitation");
        } finally {
            loadingSendInvitation.value[memberId] = false;
        }
    }

    function getGerantPhotoUrl(gerant: any): string {
        const fallback = '/public/assets/users/300w/user2.jpg';
        if (!gerant) return fallback;
        return gerant.photo_url || gerant.avatar_url || fallback;
    }

    function goToEdit() {
        router.push('/front/collectivites/ma-collectivite/edit');
    }

    const stats = computed(() => collectivite.value.stats || {});

    const secteursLabel = computed(() => {
        const s = collectivite.value.secteurs;
        if (Array.isArray(s)) return s.join(', ');
        return s || '';
    });

    const membersPercentage = computed(() => {
        const maxMembers = 50000;
        return Math.min(100, calculatePercentage(stats.value.members_count, maxMembers));
    });

    const prelevementPercentage = computed(() => {
        const current = stats.value.current_prelevement;
        if (!current) return 0;
        return calculatePercentage(current.montant_prelevement, current.montant_prelevement_expected);
    });

    const decaissementPercentage = computed(() => {
        const current = stats.value.current_decaissement;
        if (!current) return 0;
        return calculatePercentage(current.montant_decaissement, current.montant_decaissement_expected);
    });

    const activeBalancePercentage = computed(() =>
        calculatePercentage(stats.value.active_balance, stats.value.total_balance)
    );
    const blockedBalancePercentage = computed(() =>
        calculatePercentage(stats.value.blocked_balance, stats.value.total_balance)
    );

    const walletSolde = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        const totalDecaissements = stats.value.total_decaissements || 0;
        return totalPrelevements - totalDecaissements;
    });

    const walletSoldePercentage = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        if (totalPrelevements === 0) return 0;
        return calculatePercentage(walletSolde.value, totalPrelevements);
    });

    const decaissementTotalPercentage = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        if (totalPrelevements === 0) return 0;
        return calculatePercentage(stats.value.total_decaissements, totalPrelevements);
    });
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="flex align-items-center justify-content-between px-3 pb-5" style="flex-wrap: wrap; gap: 1rem;">
            <h4 class="font-semibold" style="margin: 0;">Ma collectivité</h4>
            <div class="flex gap-3" style="flex-wrap: wrap;">
                <span class="btn shadow-200 white2" :class="{ active: activeTab === 'information' }" @click="activeTab = 'information'">Information</span>
                <span class="btn shadow-200 white2" :class="{ active: activeTab === 'membres' }" @click="activeTab = 'membres'">Membres</span>
            </div>
        </div>

        <!--Collectivité-->
        <div class="s20 widget px-6 pt-8 pb-1">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ active: isCollectiviteOpen }" @click="isCollectiviteOpen = !isCollectiviteOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Collectivité
                </span>
            </div>
            <div class="s10 mt--5-5 gap-4 flex-right">
                <button class="btn lg py-3 white2 active" @click.stop="goToEdit">
                    <img src="/public/icons/icon-edit.svg" class="btn-icon" alt="" />
                    <span>Éditer</span>
                </button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="collapse-content pt-5 pb-8" :class="{ active: isCollectiviteOpen }" v-show="isCollectiviteOpen">
                <div class="collectivite-header">
                    <!-- Carte identité -->
                    <div class="collectivite-identity">
                        <div class="collectivite-logo">
                            <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/public/assets/logos/logo2.svg'" alt="logo" />
                        </div>
                        <div class="collectivite-identity-body">
                            <div class="flex align-items-center gap-3 mb-2" style="flex-wrap: wrap;">
                                <h3 style="margin: 0;">{{ collectivite.nom || '' }}</h3>
                                <span class="tag-state notif stroke">Actif</span>
                            </div>
                            <p class="text-grey-600 mb-4">{{ collectivite.rccm || 'RCCM non renseigné' }}</p>

                            <div class="collectivite-info-grid">
                                <div>
                                    <p class="text-sm text-grey-500 mb-1">Pays / Secteurs</p>
                                    <p class="font-semibold">
                                        {{ collectivite.pays?.nom || 'Côte d\'Ivoire' }} <span v-if="secteursLabel"> · {{ secteursLabel }}</span>
                                    </p>
                                </div>
                                <div v-if="collectivite.telephone">
                                    <p class="text-sm text-grey-500 mb-1">Téléphone</p>
                                    <p class="font-semibold">{{ collectivite.telephone }}</p>
                                </div>
                                <div v-if="collectivite.email">
                                    <p class="text-sm text-grey-500 mb-1">Email</p>
                                    <p class="font-semibold text-truncate">{{ collectivite.email }}</p>
                                </div>
                                <div v-if="collectivite.adresse">
                                    <p class="text-sm text-grey-500 mb-1">Adresse</p>
                                    <p class="font-semibold">{{ collectivite.adresse }}</p>
                                </div>
                                <div v-if="collectivite.date_creation || collectivite.created_at">
                                    <p class="text-sm text-grey-500 mb-1">Date de création</p>
                                    <p class="font-semibold">{{ formatDateFrench(collectivite.date_creation || collectivite.created_at) }}</p>
                                </div>
                                <div v-if="collectivite.date_souscription">
                                    <p class="text-sm text-grey-500 mb-1">Date de souscription</p>
                                    <p class="font-semibold">{{ formatDateFrench(collectivite.date_souscription) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Barres stats -->
                    <div class="collectivite-stats">
                        <div>
                            <div class="progress-bar-header mb-2">
                                <h6 class="font-semibold" style="margin: 0;">Membres</h6>
                                <span class="text-sm text-grey-500">{{ stats.members_count || 0 }} / 50 000</span>
                            </div>
                            <div class="progress-bar">
                                <div class="progress-bar-fill bg-pop-green" :style="{ width: Math.max(membersPercentage, 2) + '%' }">
                                    {{ membersPercentage }}%
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="progress-bar-header mb-2">
                                <h6 class="font-semibold" style="margin: 0;">Prélèvement {{ stats.current_month_label || '' }}</h6>
                                <span class="text-sm text-grey-500">{{ formatAmountFr(stats.current_prelevement?.montant_prelevement) }} / {{ formatAmountFr(stats.current_prelevement?.montant_prelevement_expected || 0) }}</span>
                            </div>
                            <div class="progress-bar">
                                <div class="progress-bar-fill bg-pop-green" :style="{ width: Math.max(prelevementPercentage, 2) + '%' }">
                                    {{ prelevementPercentage }}%
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="progress-bar-header mb-2">
                                <h6 class="font-semibold" style="margin: 0;">Décaissement {{ stats.current_month_label || '' }}</h6>
                                <span class="text-sm text-grey-500">{{ formatAmountFr(stats.current_decaissement?.montant_decaissement) }} / {{ formatAmountFr(stats.current_decaissement?.montant_decaissement_expected || 0) }}</span>
                            </div>
                            <div class="progress-bar">
                                <div class="progress-bar-fill bg-color-1000 text-white" :style="{ width: Math.max(decaissementPercentage, 2) + '%' }">
                                    {{ decaissementPercentage }}%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="my-7" />

                <div class="s20 flex items-center px-8 pb-2 gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Débloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.active_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ activeBalancePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: Math.max(activeBalancePercentage, 5) + '%' }">
                                {{ activeBalancePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-purple"></div>
                            Bloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.blocked_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ blockedBalancePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" :style="{ width: Math.max(blockedBalancePercentage, 5) + '%' }">
                                {{ blockedBalancePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Total
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.total_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>

        <!--Wallet-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ active: isWalletOpen }" @click="isWalletOpen = !isWalletOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Wallet / Décaissement
                </span>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 gap-6 px-5 pb-8 collapse-content" :class="{ active: isWalletOpen }" v-show="isWalletOpen">
                <div class="clear-both h-10"></div>
                <div class="flex items-center gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-black"></div>
                            Solde
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(walletSolde) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ walletSoldePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-black" :style="{ width: Math.max(walletSoldePercentage, 5) + '%' }">
                                {{ walletSoldePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Montant Décaissé
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(stats.total_decaissements) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementTotalPercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: Math.max(decaissementTotalPercentage, 5) + '%' }">
                                {{ decaissementTotalPercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Dernier décaissement
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(stats.last_decaissement?.montant_decaissement) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>

        <!--Gérant (lecture seule)-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ active: isGerantSectionOpen }" @click="isGerantSectionOpen = !isGerantSectionOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Gérant
                </span>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 collapse-content pt-3 pb-6" :class="{ active: isGerantSectionOpen }" v-show="isGerantSectionOpen">
                <div class="px-5">
                    <div v-if="collectivite.current_gerant" style="display: flex; align-items: center; gap: 1.5rem;">
                        <div style="width: 160px; height: 160px; min-width: 160px; max-width: 160px; flex: 0 0 160px; border-radius: 15px; overflow: hidden;">
                            <img :src="getGerantPhotoUrl(collectivite.current_gerant)" style="width: 160px; height: 160px; object-fit: cover; display: block;" />
                        </div>
                        <div style="flex: 1 1 auto; min-width: 0;">
                            <h3 class="mb-2">{{ collectivite.current_gerant.nom }} {{ collectivite.current_gerant.prenom }}</h3>
                            <p class="text-xl">{{ collectivite.current_gerant.email }}</p>
                            <p class="text-xl">{{ collectivite.current_gerant.telephone }}</p>
                            <p class="text-xl mt-2" v-if="collectivite.current_gerant.fonction">{{ collectivite.current_gerant.fonction }}</p>
                        </div>
                    </div>
                    <div v-else class="text-grey-500 text-center py-5">
                        Aucun gérant désigné pour cette collectivité.
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>

        <!--Membres-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ active: isMembresOpen }" @click="isMembresOpen = !isMembresOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/public/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/public/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Membres
                </span>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content" :class="{ active: isMembresOpen }" v-show="isMembresOpen">
                <div class="clear-both h-5"></div>
                <table class="table separator skin3 selection rounded">
                    <thead>
                        <tr>
                            <th><label class="btn-circle" for="check-members-all"><input type="checkbox" name="check-members-all" id="check-members-all" /></label></th>
                            <th></th>
                            <th><img src="/public/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/public/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th class="flex-right">
                                <button class="btn-ultra color2">
                                    <div class="btn-show">
                                        <img src="/public/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loadingMembers">
                            <tr>
                                <td colspan="5" class="text-center text-grey-500 py-5">
                                    <div class="loader dark"></div>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="membersList.length === 0">
                            <tr>
                                <td colspan="5" class="text-center text-grey-500 py-5">
                                    Aucun membre trouvé
                                </td>
                            </tr>
                        </template>
                        <tr class="text-grey-700 over-effect" v-for="(member, index) in membersList" :key="'member_' + index">
                            <td class="selection-item">
                                <label class="btn-circle" :for="'member_check_' + index">
                                    <input type="checkbox" name="selectedMembers[]" :id="'member_check_' + index" :value="member.id" v-model="selectedMembers" />
                                </label>
                            </td>
                            <td class="flex">
                                <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                    <img :src="member.photo_url || member.avatar_url || '/public/assets/users/300w/user2.jpg'" width="100%" />
                                </div>
                                <div class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-warning-rgb), 0.2);" v-if="collectivite.logo">
                                    <img :src="BACKEND_URL + '/assets/collectivites/' + collectivite.logo" width="100%" />
                                </div>
                            </td>
                            <td style="width: 20%;">
                                <p class="text-xl font-semibold mb-2">
                                    {{ member.reference || 'N/A' }} &nbsp;
                                    <span class="tag-state warning stroke" v-if="!member.email_verified_at">En cours</span>
                                    <span class="tag-state notif stroke" v-if="member.actif">Actif</span>
                                </p>
                                {{ member.nom + ' ' + member.prenom }} - {{ member.collectivite?.nom || collectivite.nom }}
                                <span class="tag-state success stroke ml-2" v-if="member.is_gerant">Gérant</span>
                            </td>
                            <td class="text-3xl">
                                {{ member.quotite_cessible_pourcent ? member.quotite_cessible_pourcent + '%' : '-' }}
                                <p class="mt-1">{{ member.quotite_cessible_montant ? formatAmountFr(member.quotite_cessible_montant) : '-' }}</p>
                            </td>
                            <td>
                                <div class="flex-right gap-4">
                                    <router-link :to="'/front/collectivites/membres/' + member.id + '/edit'">
                                        <button class="btn tips">
                                            <img src="/public/icons/icon-edit.svg" class="btn-icon" alt="" />
                                        </button>
                                    </router-link>
                                    <button
                                        type="button"
                                        @click="sendInvitationsUnverified(member.id)"
                                        class="btn normal ml-3 justify-content-center btn-filtre"
                                        :disabled="loadingSendInvitation[member.id]"
                                        v-if="member.can_receive_invitation_link == true"
                                    >
                                        <img src="/public/icons/icon-send.svg" class="btn-icon" alt="" />
                                        <span v-if="!loadingSendInvitation[member.id]">Envoyer invitation</span>
                                        <span v-else>Envoi...</span>
                                    </button>
                                    <router-link :to="'/front/collectivites/membres/' + member.id">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Gérer</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/front/collectivites/membres/' + member.id + '/carte'">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Carte</span>
                                        </button>
                                    </router-link>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style="display: flex; flex-direction: row; justify-content: center;" v-if="membersList.length > 0">
                    <custom-pagination
                        v-model="membersPagination.pageNumber"
                        :total="membersPagination.total"
                        :per-page="membersPagination.per_page"
                        :last_page="membersPagination.last_page"
                        @update:modelValue="onMembersPaginate"
                    />
                </div>
            </div>
        </div>

        <div class="clear-both h-50"></div>

        <confirmation-modal ref="confirmationModalRef" />
    </section>
</template>

<style scoped>
.collectivite-header {
    display: flex;
    gap: 2.5rem;
    padding: 0 1.5rem;
    flex-wrap: wrap;
    align-items: flex-start;
}

.collectivite-identity {
    flex: 1 1 420px;
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
}

.collectivite-logo {
    flex: 0 0 140px;
    width: 140px;
    height: 140px;
    border-radius: 20px;
    overflow: hidden;
    background-color: rgba(var(--color-black-rgb), 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
}

.collectivite-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.collectivite-identity-body {
    flex: 1 1 auto;
    min-width: 0;
}

.collectivite-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem 1.5rem;
    margin-top: 0.5rem;
}

.collectivite-info-grid p {
    margin: 0;
    word-break: break-word;
}

.collectivite-stats {
    flex: 1 1 360px;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.progress-bar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.progress-bar {
    height: 40px;
    border-radius: 100px;
    background-color: rgba(var(--color-black-rgb), 0.08);
    overflow: hidden;
    position: relative;
}

.progress-bar-fill {
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 1.25rem;
    border-radius: 100px;
    font-size: 0.95rem;
    font-weight: 600;
    transition: width 0.3s ease;
    white-space: nowrap;
}

.text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
